<template>
  <div class="board-frame">
    <div class="board-glow" />
    <div class="board-wrap" :style="wrapStyle">
      <div class="board" :style="gridStyle">
        <template v-for="(row, r) in board" :key="'r' + r">
          <button
            v-for="(cell, c) in row"
            :key="`${r}-${c}`"
            type="button"
            class="cell"
            :class="cellClass(r, c, cell)"
            :disabled="!canPlayCell(r, c)"
            @click="onCellClick(r, c)"
          >
            <span
              v-if="cell !== 0"
              class="disc"
              :class="cell === 1 ? 'black' : 'white'"
            />
            <span v-else-if="showHint(r, c)" class="hint" />
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { EMPTY } from '../game/constants'

export default {
  name: 'OthelloBoard',
  props: {
    board: { type: Array, required: true },
    legalMoves: { type: Array, default: () => [] },
    interactive: { type: Boolean, default: true },
    lastMove: { type: Array, default: null },
    highlightLegal: { type: Boolean, default: true },
  },
  emits: ['move'],
  computed: {
    size() {
      return this.board.length
    },
    legalSet() {
      const s = new Set()
      for (const [r, c] of this.legalMoves) s.add(`${r},${c}`)
      return s
    },
    cellPx() {
      const s = this.size
      if (s <= 6) return 56
      if (s <= 8) return 50
      if (s <= 10) return 44
      return 38
    },
    wrapStyle() {
      return { '--cell': `${this.cellPx}px` }
    },
    gridStyle() {
      return {
        gridTemplateColumns: `repeat(${this.size}, var(--cell))`,
        gridTemplateRows: `repeat(${this.size}, var(--cell))`,
      }
    },
  },
  methods: {
    showHint(r, c) {
      return this.highlightLegal && this.legalSet.has(`${r},${c}`)
    },
    canPlayCell(r, c) {
      if (!this.interactive) return false
      return this.legalSet.has(`${r},${c}`)
    },
    cellClass(r, c, cell) {
      const last =
        this.lastMove &&
        this.lastMove[0] === r &&
        this.lastMove[1] === c
      return {
        empty: cell === EMPTY,
        playable: this.canPlayCell(r, c),
        last,
      }
    },
    onCellClick(r, c) {
      if (!this.canPlayCell(r, c)) return
      this.$emit('move', r, c)
    },
  },
}
</script>

<style scoped>
.board-frame {
  position: relative;
  padding: 4px;
}

.board-glow {
  position: absolute;
  inset: -20px;
  background: radial-gradient(
    ellipse at center,
    rgba(16, 185, 129, 0.15) 0%,
    transparent 70%
  );
  pointer-events: none;
  z-index: 0;
}

.board-wrap {
  position: relative;
  z-index: 1;
  display: inline-block;
  padding: 14px;
  background: linear-gradient(160deg, #14532d 0%, #052e16 50%, #0a1f12 100%);
  border-radius: 20px;
  box-shadow:
    0 25px 60px rgba(0, 0, 0, 0.55),
    0 0 0 1px rgba(255, 255, 255, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
}

.board {
  display: grid;
  gap: 3px;
  background: #022c15;
  padding: 6px;
  border-radius: 12px;
  box-shadow: inset 0 2px 12px rgba(0, 0, 0, 0.4);
}

.cell {
  width: var(--cell);
  height: var(--cell);
  border: none;
  padding: 0;
  margin: 0;
  cursor: default;
  background: linear-gradient(145deg, #2d6a4f, #1b4332);
  border-radius: 6px;
  position: relative;
  transition:
    background 0.18s ease,
    transform 0.12s ease,
    box-shadow 0.18s ease;
}

.cell.playable {
  cursor: pointer;
  background: linear-gradient(145deg, #40916c, #2d6a4f);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.15);
}

.cell.playable:hover {
  background: linear-gradient(145deg, #52b788, #40916c);
  transform: scale(1.04);
  box-shadow: 0 4px 16px rgba(82, 183, 136, 0.35);
}

.cell.last {
  box-shadow:
    inset 0 0 0 2px rgba(251, 191, 36, 0.9),
    0 0 12px rgba(251, 191, 36, 0.35);
}

.disc {
  position: absolute;
  inset: 10%;
  border-radius: 50%;
  animation: pop 0.25s ease-out;
}

@keyframes pop {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

.disc.black {
  background: radial-gradient(circle at 32% 28%, #4a4a4a, #0a0a0a 72%);
  box-shadow:
    0 4px 10px rgba(0, 0, 0, 0.5),
    inset 0 -2px 4px rgba(255, 255, 255, 0.08);
}

.disc.white {
  background: radial-gradient(circle at 32% 28%, #ffffff, #d4d4d4 68%);
  box-shadow:
    0 4px 10px rgba(0, 0, 0, 0.25),
    inset 0 -2px 4px rgba(0, 0, 0, 0.06);
}

.hint {
  position: absolute;
  width: 20%;
  height: 20%;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.4);
  pointer-events: none;
}

.cell.playable .hint {
  background: rgba(255, 255, 255, 0.45);
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.3);
}
</style>